import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';

import type { EventData } from 'webapp-types';
import { AppEvent } from 'webapp/enums/events';

import didInsert from 'webapp/modifiers/did-insert';
import willDestroy from 'webapp/modifiers/will-destroy';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import type { Args } from 'webapp/components/forecasts/data/tooltip/base';
import ForecastsDataTooltipBaseComponent from 'webapp/components/forecasts/data/tooltip/base';

import { historyPlotlineClassName } from 'webapp/d-chart/renderer/forecast-chart';

export default class ForecastsDataTooltipHistoryComponent extends ForecastsDataTooltipBaseComponent<Args> {
	@tracked isOverflowingForecastTooltip = false;

	seriesLineClass = historyPlotlineClassName;

	get arrowPosition(): string {
		return this.isOverflowingForecastTooltip ? 'top' : 'bottom';
	}

	@action
	reposition(data: EventData.ByName[AppEvent.REPOSITION_FORECAST_HISTORY_TOOLTIP]): void {
		if (this.isExplicitlyClosed) {
			return;
		}

		const plotLinePosition = this.getPlotLinePosition();
		if (!plotLinePosition) {
			this.isAllPlotlinesHidden = true;
			return;
		}

		this.isAllPlotlinesHidden = !plotLinePosition.height && !plotLinePosition.width;

		// when inside a popup with transform, position:fixed is relative to the popup
		const offset = this.getContainingBlockOffset();

		const additionalPointMarginTop = this.additionalPointMargin,
			componentHeight = this.getComponentHeight(),
			componentWidth = this.getComponentWidth(),
			calculatedTop =
				plotLinePosition.top - offset.top - componentHeight - additionalPointMarginTop,
			calculatedLeft = plotLinePosition.left - offset.left - componentWidth / 2;

		this.isOverflowingForecastTooltip =
			data.topLimit > calculatedTop - componentHeight &&
			data.leftLimit < calculatedLeft + componentWidth &&
			data.rightLimit > calculatedLeft;

		this.top = this.isOverflowingForecastTooltip
			? plotLinePosition.top - offset.top + additionalPointMarginTop
			: calculatedTop;
		this.left = calculatedLeft;
	}

	@action
	override setupView(node: HTMLElement): void {
		super.setupView(node);

		this.eventManagerService.on(
			AppEvent.REPOSITION_FORECAST_HISTORY_TOOLTIP,
			this,
			this.reposition,
		);
	}

	@action
	teardownView(): void {
		this.eventManagerService.off(AppEvent.REPOSITION_FORECAST_HISTORY_TOOLTIP, this);
	}
	<template>
		<div
			class="history-value-tooltip {{unless this.isVisible 'hidden'}}"
			style={{this.style}}
			{{didInsert this.setupView}}
			{{willDestroy this.teardownView}}
		>
			<div class="highcharts-dbx-tooltip highcharts-dbx-tooltip--forecast">
				<div class="top">
					<div class="tooltip-title">{{this.timeLabel}}</div>
				</div>
				<div class="point-ct point-ct--primary">
					<span class="value">{{@model.valueStr}}</span>
				</div>
			</div>
			<div class="arrow arrow--{{this.arrowPosition}}"></div>

			<a
				role="button"
				class="close"
				{{on "click" this.handleCloseClick}}
			><CommonUiIconComponent @name="Close" @size="sm" />
			</a>

		</div>
	</template>
}
